//src/admin/pages/AdminCreateOrderDiscount.js
import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { FaBox, FaCalendarAlt, FaClock } from "react-icons/fa";
import { FiChevronRight, FiAlertCircle } from "react-icons/fi";
import { createAdminDiscount } from "../../services/adminProducts.api";
import { useParams } from "react-router-dom";
import { getAdminDiscountById, updateAdminDiscount } from "../../services/adminDiscounts.api";

function AdminCreateOrderDiscount() {
  const { id } = useParams();
  const navigate = useNavigate();
  const initialRef = React.useRef(null);
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);

  // ================= STATE =================
  const [discountType, setDiscountType] = useState("percentage");
  
  const [isDirty, setIsDirty] = useState(false);
  
  const [discountValue, setDiscountValue] = useState("");

  const [minRequirement, setMinRequirement] = useState("none");
  const [minAmount, setMinAmount] = useState("");
  const [minQty, setMinQty] = useState("");

  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [hasEnd, setHasEnd] = useState(false);
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");

  const [isActive, setIsActive] = useState(1);

  const [toast, setToast] = useState(null);

  // DATE MODAL
const [dateModalOpen, setDateModalOpen] = useState(false);
const [dateTarget, setDateTarget] = useState(null);

// TIME MODAL
const [timeModalOpen, setTimeModalOpen] = useState(false);
const [timeTarget, setTimeTarget] = useState(null);

// TEMP DATE/TIME
const [rangeStart, setRangeStart] = useState(null);
const [rangeEnd, setRangeEnd] = useState(null);

const [tempTime, setTempTime] = useState({
  hour: 12,
  minute: "00",
  period: "AM",
});

const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
const [pendingNavigation, setPendingNavigation] = useState(null);

const handleNavigate = (path) => {
  if (!isDirty) {
    navigate(path);
  } else {
    setPendingNavigation(() => () => navigate(path));
    setShowLeaveConfirm(true);
  }
};

  // ================= UI =================
  const card =
  "bg-white border border-[#e1e3e5] rounded-[18px] shadow-sm p-4 space-y-4";
  const subtitle = "text-[13px] text-[#6d7175]";

  const input =
    "w-full h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm outline-none bg-white text-[#202223] focus:border-[#008060]";

  const select =
    "appearance-none w-full h-[42px] rounded-[12px] border border-[#c9cccf] px-3 text-sm bg-white";

    const formatDisplayDate = (date) => {
  if (!date) return new Date().toLocaleDateString();
  return new Date(date).toLocaleDateString();
};

const handleSave = async () => {
  try {
    const start_at = startDate
      ? `${startDate} ${startTime || "00:00"}:00`
      : null;

    const end_at =
      hasEnd && endDate
        ? `${endDate} ${endTime || "00:00"}:00`
        : null;

    const payload = {
      type: "order",
      discountType,
      discountValue: Number(discountValue),
      minRequirement,
      minAmount: Number(minAmount || 0),
      minQty: Number(minQty || 0),
      start_at,
      end_at,
      is_active: isActive,
    };

    // ✅ CREATE OR UPDATE (🔥 FIX)
    if (id) {
      await updateAdminDiscount(id, payload);
    } else {
      await createAdminDiscount(payload);
    }

    setToast(
      lang === "AR"
        ? "تم حفظ الخصم بنجاح"
        : "Order discount saved successfully"
    );

    setTimeout(() => {
      navigate("/admin/discounts");
    }, 1200);

  } catch (err) {
    console.error(err);
  }
};

const formatDisplayTime = (time) => {
  if (!time) {
    return new Date().toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  const [h, m] = time.split(":");
  const d = new Date();
  d.setHours(h, m);

  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

useEffect(() => {
  if (!initialRef.current) return;

    // 🚀 prevent early dirty calculation during load
  if (id && discountValue === "") return;

  const current = {
  isActive,
  discountType,
  discountValue: String(discountValue || ""),
  minRequirement,
  minAmount: String(minAmount || ""),
  minQty: String(minQty || ""),
  startDate,
  startTime,
  endDate,
  endTime,
  hasEnd,
};

  const initial = initialRef.current;

  const isChanged = Object.keys(initial).some(
    key => JSON.stringify(initial[key]) !== JSON.stringify(current[key])
  );

  setIsDirty(isChanged);
}, [
  isActive,
  discountType,
  discountValue,
  minRequirement,
  minAmount,
  minQty,
  startDate,
  startTime,
  endDate,
  endTime,
  hasEnd,
]);

useEffect(() => {
  if (!id) {
    initialRef.current = {
      discountType: "percentage",
      discountValue: "",
      minRequirement: "none",
      minAmount: "",
      minQty: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      hasEnd: false,
    };
  }
}, [id]);

useEffect(() => {
  if (!id) return;

  getAdminDiscountById(id).then((d) => {
    setIsActive(d.is_active ?? 1);
    setDiscountType(d.discount_type);
    setDiscountValue(d.discount_value);

    setMinRequirement(d.min_requirement || "none");
    setMinAmount(d.min_amount || "");
    setMinQty(d.min_qty || "");

    if (d.start_at) {
  const dt = new Date(d.start_at);

  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");

  const hh = String(dt.getHours()).padStart(2, "0");
  const min = String(dt.getMinutes()).padStart(2, "0");

  setStartDate(`${yyyy}-${mm}-${dd}`);
  setStartTime(`${hh}:${min}`);
}

    if (d.end_at) {
  const dt = new Date(d.end_at);

  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");

  const hh = String(dt.getHours()).padStart(2, "0");
  const min = String(dt.getMinutes()).padStart(2, "0");

  setEndDate(`${yyyy}-${mm}-${dd}`);
  setEndTime(`${hh}:${min}`);
  setHasEnd(true);
}

initialRef.current = {
  isActive: d.is_active ?? 1,
  discountType: d.discount_type || "percentage",
  discountValue: String(d.discount_value || ""),
  minRequirement: d.min_requirement || "none",
  minAmount: d.min_amount != null ? String(d.min_amount) : "",
  minQty: d.min_qty != null ? String(d.min_qty) : "",
  startDate: d.start_at ? new Date(d.start_at).toISOString().slice(0, 10) : "",
  startTime: d.start_at ? new Date(d.start_at).toTimeString().slice(0, 5) : "",
  endDate: d.end_at ? new Date(d.end_at).toISOString().slice(0, 10) : "",
  endTime: d.end_at ? new Date(d.end_at).toTimeString().slice(0, 5) : "",
  hasEnd: !!d.end_at,
};

setIsDirty(false);

  }).catch(console.error);
}, [id]);

  // ================= SUMMARY =================
  const buildSummary = () => {
  if (!discountValue) {
    return lang === "AR"
      ? "ابدأ في إعداد الخصم..."
      : "Start configuring your discount...";
  }

  // VALUE
  const valueText =
    discountType === "percentage"
      ? (lang === "AR"
          ? `خصم ${discountValue}%`
          : `${discountValue}% off`)
      : (lang === "AR"
          ? `خصم ${discountValue}$`
          : `$${discountValue} off`);

  // MIN REQUIREMENT
  let minText = "";

  if (minRequirement === "none") {
    minText = lang === "AR"
      ? "جميع الطلبات"
      : "all orders";
  }

  if (minRequirement === "amount" && minAmount) {
    minText = lang === "AR"
      ? `طلبات بقيمة أكثر من ${minAmount}$`
      : `orders over $${minAmount}`;
  }

  if (minRequirement === "qty" && minQty) {
    minText = lang === "AR"
      ? `طلبات تحتوي على ${minQty}+ عناصر`
      : `orders with ${minQty}+ items`;
  }

  // FINAL
  return lang === "AR"
    ? `هذا الخصم يقدم ${valueText} على ${minText}.`
    : `This discount gives ${valueText} on ${minText}.`;
};

  const CalendarRangePicker = ({ rangeStart, rangeEnd, setRangeStart, setRangeEnd }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

  const startDay = startOfMonth.getDay();

  const days = [];

  for (let i = 0; i < startDay; i++) days.push(null);

  for (let d = 1; d <= endOfMonth.getDate(); d++) {
    days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d));
  }

  const isSameDay = (a, b) =>
    a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const handleClick = (day) => {
    setRangeStart(day);
    setRangeEnd(day);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3 px-2">
        <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}>‹</button>

        <div className="font-semibold text-sm">
          {currentMonth.toLocaleString("en", {
            month: "long",
            year: "numeric",
          })}
        </div>

        <button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}>›</button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day, i) => {
          if (!day) return <div key={i} />;

          const selected = isSameDay(day, rangeStart);

          return (
            <button
              key={i}
              onClick={() => handleClick(day)}
              className={`h-8 rounded-md text-sm ${
                selected ? "bg-black text-white" : "hover:bg-gray-200"
              }`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TimePicker = ({ tempTime, setTempTime }) => {
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = ["00", "15", "30", "45"];
  const periods = ["AM", "PM"];

  return (
    <div className="flex gap-3">

      {/* HOURS */}
      <div className="flex-1">
        <div className="text-xs text-gray-500 mb-2">Hour</div>
        <div className="grid grid-cols-3 gap-2">
          {hours.map(h => (
            <button
              key={h}
              onClick={() => setTempTime(prev => ({ ...prev, hour: h }))}
              className={`h-9 rounded-lg text-sm ${
                tempTime.hour === h ? "bg-black text-white" : "hover:bg-gray-200"
              }`}
            >
              {h}
            </button>
          ))}
        </div>
      </div>

      {/* MINUTES */}
      <div className="flex-1">
        <div className="text-xs text-gray-500 mb-2">Min</div>
        <div className="flex flex-col gap-2">
          {minutes.map(m => (
            <button
              key={m}
              onClick={() => setTempTime(prev => ({ ...prev, minute: m }))}
              className={`h-9 rounded-lg text-sm ${
                tempTime.minute === m ? "bg-black text-white" : "hover:bg-gray-200"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* AM / PM */}
      <div className="flex flex-col gap-2">
        {periods.map(p => (
          <button
            key={p}
            onClick={() => setTempTime(prev => ({ ...prev, period: p }))}
            className={`h-9 px-3 rounded-lg text-sm ${
              tempTime.period === p ? "bg-black text-white" : "hover:bg-gray-200"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

    </div>
  );
};

  return (
    <div className={`flex bg-[#f1f1f1] min-h-screen`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />


      <div className={`flex-1 ${lang === "EN" ? "lg:ml-64" : ""}`}>
        <AdminHeader
  setIsOpen={setIsOpen}
  customCenter={
    <div
      className={`
        hidden sm:block
        w-full
        sm:max-w-[500px]
        max-w-full
        transition-all duration-300
        ${isDirty ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"}
      `}
    >
      <div className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-md px-3 py-2 sm:py-[4px] space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between">

        {/* LEFT */}
        <div className="flex items-center gap-2 text-[13px] text-gray-200 justify-between sm:justify-start">
          <FiAlertCircle className="text-white text-[16px]" />
          <span>
            {lang === "AR"
  ? id ? "تعديلات غير محفوظة" : "خصم غير محفوظ"
  : id ? "Unsaved changes" : "Unsaved discount"}
          </span>
        </div>

        {/* RIGHT */}
        <div className="flex gap-2 sm:gap-1.5 w-full sm:w-auto">

          {/* DISCARD */}
          <button
            type="button"
            onClick={() => handleNavigate("/admin/discounts")}
            className="
              px-2 py-[2px]
              text-[12px]
              rounded-md
              border border-[#3a3a3a]
              bg-[#1f1f1f]
              hover:bg-[#363636]
              text-gray-200
              transition
            "
          >
            {lang === "AR" ? "إلغاء" : "Discard"}
          </button>

          {/* SAVE */}
          <button
            onClick={handleSave}
            type="button"
            className="
              px-2 py-[2px]
              text-[12px]
              rounded-md
              font-medium
              bg-white text-black hover:bg-gray-200
              transition
            "
          >
            {lang === "AR" ? "حفظ" : "Save"}
          </button>

        </div>
      </div>
    </div>
  }
/>

        <main className="px-3 py-4 sm:p-6">
          <div className="max-w-[1040px] mx-auto">

            {/* HEADER */}
            <div className="flex items-center gap-2 mb-5">
              <button
  onClick={() => handleNavigate("/admin/discounts")}
  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-200"
>
  <img
    src="/icons/discountNotSelected.png"
    alt="discount"
    className="w-[20px] h-[20px] object-contain"
  />
</button>

              <FiChevronRight />

              <span className="font-semibold text-[#202223]">
  {id
    ? (lang === "AR" ? "تعديل خصم الطلب" : "Edit order discount")
    : (lang === "AR" ? "خصم على الطلب" : "Amount off order")}
</span>
            </div>

<div className="sm:hidden mb-4 flex gap-2">
  <button
    type="button"
    onClick={() => handleNavigate("/admin/discounts")}
    className="flex-1 h-[42px] rounded-[12px] border border-[#c9cccf] bg-white text-[#202223] font-medium"
  >
    {lang === "AR" ? "إلغاء" : "Discard"}
  </button>

  <button
    type="button"
    onClick={handleSave}
    className="flex-1 h-[42px] rounded-[12px] font-medium bg-[#202223] text-white hover:bg-black"
  >
    {lang === "AR" ? "حفظ" : "Save"}
  </button>
</div>

            {/* GRID */}
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-3">

              {/* ================= LEFT ================= */}
              <div className="space-y-3">

                {/* DISCOUNT VALUE */}
                <div className={card}>
                  <h3 className="text-[15px] font-semibold">
                     {lang === "AR" ? "قيمة الخصم" : "Discount value"}
                  </h3>

                  <div className="flex gap-3 items-center">
                    <div className="flex border border-[#c9cccf] rounded-[12px] overflow-hidden h-[42px]">

  {/* PERCENTAGE */}
  <button
  type="button"
  onClick={() => {
    setDiscountType("percentage");
  }}
  className={`
    px-3 sm:px-4
    text-xs sm:text-sm
    whitespace-nowrap
    font-medium
    transition
    ${discountType === "percentage"
      ? "bg-[#e5e5e5] text-[#202223]"
      : "bg-white text-[#202223] hover:bg-[#f6f6f7]"}
  `}
>
   {lang === "AR" ? "نسبة" : "Percentage"}
</button>

  {/* DIVIDER */}
  <div className="w-[1px] bg-[#c9cccf]" />

  {/* FIXED */}
  <button
  type="button"
  onClick={() => {
    setDiscountType("fixed");
  }}
  className={`
    px-3 sm:px-4
    text-xs sm:text-sm
    whitespace-nowrap
    font-medium
    transition
    ${discountType === "fixed"
      ? "bg-[#e5e5e5] text-[#202223]"
      : "bg-white text-[#202223] hover:bg-[#f6f6f7]"}
  `}
>
  {lang === "AR" ? "مبلغ ثابت" : "Fixed amount"}
</button>

</div>

                    <div className="relative w-[140px]">
                      <input
                        type="number"
                        value={discountValue}
                        onChange={(e) => {
                          setDiscountValue(e.target.value);
                         
                        }}
                        className={input}
                        placeholder="0"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {discountType === "fixed" ? "$" : "%"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* MIN REQUIREMENT */}
                <div className={card}>
                  <h3 className="text-[15px] font-semibold">
                      {lang === "AR" ? "الحد الأدنى للطلب" : "Minimum requirements"}
                  </h3>

                 <div className="space-y-2">

  {/* NONE */}
  <label className="flex items-center gap-3 cursor-pointer">
    <input
      type="radio"
      name="minRequirement"
      value="none"
      checked={minRequirement === "none"}
      onChange={() => {
        setMinRequirement("none");
      }}
    />
    <div>
  <span className="text-sm font-medium">
    {lang === "AR" ? "بدون حد أدنى" : "No minimum requirements"}
  </span>
  <p className="text-[12px] text-[#6d7175]">
   {lang === "AR" ? "ينطبق على جميع الطلبات" : "This discount applies to all orders"}
  </p>
</div>
  </label>

  {/* AMOUNT */}
  <label className="flex items-start gap-3 cursor-pointer">
    <input
      type="radio"
      name="minRequirement"
      value="amount"
      checked={minRequirement === "amount"}
      onChange={() => {
        setMinRequirement("amount");
      }}
      className="mt-1"
    />

    <div className="flex-1 space-y-2">
  <div>
    <span className="text-sm font-medium">
     {lang === "AR" ? "حد أدنى لقيمة الطلب" : "Minimum purchase amount"}
    </span>
    <p className="text-[12px] text-[#6d7175]">
      {lang === "AR" ? "يجب أن يصل الطلب إلى حد أدنى" : "Customers must spend a minimum amount to use this discount"}
    </p>
  </div>
      {minRequirement === "amount" && (
        <div className="relative max-w-[200px]">
          <input
            type="number"
            placeholder="0"
            value={minAmount}
            onChange={(e) => {
              setMinAmount(e.target.value);
            }}
            className={input}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm">
            $
          </span>
        </div>
      )}
    </div>
  </label>

  {/* QTY */}
  <label className="flex items-start gap-3 cursor-pointer">
    <input
      type="radio"
      name="minRequirement"
      value="qty"
      checked={minRequirement === "qty"}
      onChange={() => {
        setMinRequirement("qty");
      }}
      className="mt-1"
    />

    <div className="flex-1 space-y-2">
  <div>
    <span className="text-sm font-medium">
      {lang === "AR" ? "حد أدنى لعدد المنتجات" : "Minimum quantity of items"}
    </span>
    <p className="text-[12px] text-[#6d7175]">
      {lang === "AR" ? "يجب شراء عدد معين من المنتجات" : "Customers must buy a minimum number of items"}
    </p>
  </div>

      {minRequirement === "qty" && (
        <input
          type="number"
          placeholder="0"
          value={minQty}
          onChange={(e) => {
            setMinQty(e.target.value);
          }}
          className={"max-w-[200px] " + input}
        />
      )}
    </div>
  </label>

</div>
                </div>

                {/* DATES */}
                <div className={card}>
  <h3 className="text-[15px] font-semibold">
    Active dates
  </h3>

  <div className="space-y-3">

    {/* START */}
   <div>
  <label className="text-xs text-[#6d7175] mb-1 block">
    {lang === "AR" ? "تاريخ البدء" : "Start date"}
  </label>

  <div className="grid grid-cols-2 gap-3">
    <button
      type="button"
      onClick={() => {
        setDateTarget("start");
        setDateModalOpen(true);
      }}
      className="
        min-w-0
        flex items-center gap-2
        h-[42px]
        rounded-[12px]
        border border-[#c9cccf]
        px-3
        text-sm
        bg-white
        hover:border-[#008060]
      "
    >
      <FaCalendarAlt className="shrink-0 text-[#6d7175]" />
      <span className="truncate">
        {formatDisplayDate(startDate)}
      </span>
    </button>

    <button
      type="button"
      onClick={() => {
        setTimeTarget("start");
        setTempTime({ hour: 12, minute: "00", period: "AM" });
        setTimeModalOpen(true);
      }}
      className="
        min-w-0
        flex items-center gap-2
        h-[42px]
        rounded-[12px]
        border border-[#c9cccf]
        px-3
        text-sm
        bg-white
        hover:border-[#008060]
      "
    >
      <FaClock className="shrink-0 text-[#6d7175]" />
      <span className="truncate">
        {formatDisplayTime(startTime)}
      </span>
    </button>
  </div>
</div>

    {/* END TOGGLE */}
    <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
      <input
        type="checkbox"
        checked={hasEnd}
        onChange={() => {
          setHasEnd(!hasEnd);
        }}
      />
      Set end date
    </label>

    {/* END DATE */}
   {hasEnd && (
  <div className="mt-3">
    <label className="text-xs text-[#6d7175] mb-1 block">
      {lang === "AR" ? "تاريخ الانتهاء" : "End date"}
    </label>

    <div className="grid grid-cols-2 gap-3">
      <button
        type="button"
        onClick={() => {
          setDateTarget("end");
          setDateModalOpen(true);
        }}
        className="
          min-w-0
          flex items-center gap-2
          h-[42px]
          rounded-[12px]
          border border-[#c9cccf]
          px-3
          text-sm
          bg-white
          hover:border-[#008060]
        "
      >
        <FaCalendarAlt className="shrink-0 text-[#6d7175]" />
        <span className="truncate">
          {formatDisplayDate(endDate)}
        </span>
      </button>

      <button
        type="button"
        onClick={() => {
          setTimeTarget("end");
          setTempTime({ hour: 12, minute: "00", period: "AM" });
          setTimeModalOpen(true);
        }}
        className="
          min-w-0
          flex items-center gap-2
          h-[42px]
          rounded-[12px]
          border border-[#c9cccf]
          px-3
          text-sm
          bg-white
          hover:border-[#008060]
        "
      >
        <FaClock className="shrink-0 text-[#6d7175]" />
        <span className="truncate">
          {formatDisplayTime(endTime)}
        </span>
      </button>
    </div>
  </div>
)}

  </div>
</div>

              </div>

              {/* ================= RIGHT ================= */}
              <div className="space-y-3">
{/* STATUS (EDIT ONLY) */}
{id && (
  <div className={card}>
    <h3 className="text-[15px] font-semibold text-[#202223]">
      {lang === "AR" ? "الحالة" : "Status"}
    </h3>

    <p className={subtitle}>
      {lang === "AR"
        ? "حدد ما إذا كان الخصم نشطًا أو غير نشط"
        : "Control whether this discount is active or inactive"}
    </p>

    <div className="relative">
      <select
        value={isActive ? "active" : "inactive"}
        onChange={(e) => {
          setIsActive(e.target.value === "active" ? 1 : 0);
        }}
        className={select}
      >
        <option value="active">
          {lang === "AR" ? "نشط" : "Active"}
        </option>

        <option value="inactive">
          {lang === "AR" ? "غير نشط" : "Inactive"}
        </option>
      </select>

      {/* Shopify arrow */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6d7175]">
        ▾
      </div>
    </div>

    {/* BADGE */}
    <div className="mt-2">
      <span
        className={`
          text-[11px] px-2.5 py-[3px] rounded-md font-medium
          ${isActive
            ? "bg-green-50 text-green-700"
            : "bg-gray-100 text-gray-600"}
        `}
      >
        {isActive
          ? (lang === "AR" ? "نشط" : "Active")
          : (lang === "AR" ? "غير نشط" : "Inactive")}
      </span>
    </div>
  </div>
)}
                <div className={card}>
                  <h3 className="text-[15px] font-semibold"> {lang === "AR" ? "الملخص" : "Summary"}</h3>

                  <div className="bg-[#f6f6f7] border rounded-[14px] p-4 text-sm">
                    {buildSummary()}
                  </div>
                </div>

              </div>

            </div>
          </div>
            {dateModalOpen && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
  onClick={() => setDateModalOpen(false)}>
    <div
  className="w-[340px] bg-white rounded-[18px] shadow-[0_30px_60px_rgba(0,0,0,0.16)] overflow-hidden"
  onClick={(e) => e.stopPropagation()}
>

      {/* HEADER */}
      <div className="px-5 py-4 border-b">
        <h3 className="text-sm font-semibold">
          {lang === "AR" ? "اختر التاريخ" : "Select date"}
        </h3>
      </div>

      {/* BODY */}
      <div className="p-4">
        <CalendarRangePicker
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          setRangeStart={setRangeStart}
          setRangeEnd={setRangeEnd}
        />
      </div>

      {/* FOOTER */}
      <div className="px-4 py-3 flex justify-end gap-2 border-t">

        <button
          onClick={() => setDateModalOpen(false)}
          className="h-[36px] px-4 border rounded-lg"
        >
          Cancel
        </button>

        <button
          onClick={() => {
            if (!rangeStart) return;

            const date = `${rangeStart.getFullYear()}-${String(rangeStart.getMonth()+1).padStart(2,"0")}-${String(rangeStart.getDate()).padStart(2,"0")}`;

            if (dateTarget === "start") setStartDate(date);
            if (dateTarget === "end") setEndDate(date);

            setDateModalOpen(false);
          }}
          className="h-[36px] px-4 bg-black text-white rounded-lg"
        >
          Apply
        </button>

      </div>
    </div>
  </div>
)}

{timeModalOpen && (
 <div
  className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
  onClick={() => setTimeModalOpen(false)}
>
    <div
  className="w-[320px] bg-white rounded-[18px] shadow-[0_30px_60px_rgba(0,0,0,0.16)]"
  onClick={(e) => e.stopPropagation()}
>

      {/* HEADER */}
      <div className="px-5 py-4 border-b">
        <h3 className="text-sm font-semibold">
          {lang === "AR" ? "اختر الوقت" : "Select time"}
        </h3>
      </div>

      {/* BODY */}
      <div className="p-4">
        <TimePicker
            tempTime={tempTime}
            setTempTime={setTempTime}
          />
      </div>

      {/* FOOTER */}
      <div className="px-4 py-3 flex justify-end">
        <button
          onClick={() => {
            let hour = tempTime.hour;

            if (tempTime.period === "PM" && hour !== 12) hour += 12;
            if (tempTime.period === "AM" && hour === 12) hour = 0;

            const final = `${String(hour).padStart(2, "0")}:${tempTime.minute}`;

            if (timeTarget === "start") setStartTime(final);
            if (timeTarget === "end") setEndTime(final);

            setTimeModalOpen(false);
          }}
          className="h-[36px] px-4 bg-black text-white rounded-lg"
        >
          Done
        </button>
      </div>

    </div>
  </div>
)}

{toast && (
  <div
    className="
      fixed bottom-6 right-6
      bg-[#1f1f1f] text-white
      px-4 py-2
      rounded-lg
      text-sm
      shadow-lg
      z-[9999]
      animate-fadeSlide
    "
  >
    {toast}
  </div>
)}

{showLeaveConfirm && (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
    <div className="w-full max-w-[420px] bg-white rounded-[18px] border shadow-xl">

      <div className="px-5 py-4 border-b">
        <h3 className="text-lg font-semibold">
          {lang === "AR" ? "مغادرة الصفحة؟" : "Leave page?"}
        </h3>
      </div>

      <div className="p-5 text-sm">
        {lang === "AR"
          ? "لديك تغييرات غير محفوظة. إذا غادرت سيتم فقدانها."
          : "You have unsaved changes. If you leave, they will be lost."}
      </div>

      <div className="px-5 py-4 border-t flex justify-end gap-3">

        <button
          onClick={() => setShowLeaveConfirm(false)}
          className="px-4 py-2 border rounded"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={() => {
            setShowLeaveConfirm(false);
            pendingNavigation && pendingNavigation();
          }}
          className="px-4 py-2 bg-black text-white rounded"
        >
          {lang === "AR" ? "مغادرة" : "Leave"}
        </button>

      </div>
    </div>
  </div>
)}

        </main>
      </div>
    </div>
  );
}

export default AdminCreateOrderDiscount;